import { STAGE, type LABEL_SIDES } from '@atnt/shared';
import { clamp } from '../units';

/**
 * Coordinate maths of the constructor (docs/design/02-constructor.md §8).
 *  - Stage space: design px of the 1920×1080 dashboard stage.
 *  - Drawing space: (u, v) ∈ [0, 1]² of the drawing's natural size.
 * Everything persisted is in one of these two spaces; nothing depends on the viewport, zoom or panel layout.
 */

export interface Point {
  x: number;
  y: number;
}
export interface Size {
  w: number;
  h: number;
}
export interface ClientPoint {
  clientX: number;
  clientY: number;
}

/** `contain` = uniform letterbox fit; `width` = fit the width, scroll vertically when taller than the host. */
export type StageFit = 'contain' | 'width';

export interface StageFitResult {
  /** CSS px per design px */
  s: number;
  /** offset of the stage's top-left corner inside the host, CSS px */
  ox: number;
  oy: number;
}

/** Uniform fit of a `W×H` stage into a `vw×vh` host. */
export function fitStage(vw: number, vh: number, fit: StageFit = 'contain', W: number = STAGE.width, H: number = STAGE.height): StageFitResult {
  const s = fit === 'contain' ? Math.min(vw / W, vh / H) : vw / W;
  const safe = Number.isFinite(s) && s > 0 ? s : 1;
  return { s: safe, ox: (vw - W * safe) / 2, oy: fit === 'contain' ? (vh - H * safe) / 2 : 0 };
}

/**
 * Pointer → stage px. Works under any CSS transform because `getBoundingClientRect` is post-transform.
 * `s` is the current effective scale (CSS px per design px).
 */
export function clientToStage(e: ClientPoint, stageEl: Element, W: number = STAGE.width): Point & { s: number } {
  const r = stageEl.getBoundingClientRect();
  const s = r.width / W || 1;
  return { x: (e.clientX - r.left) / s, y: (e.clientY - r.top) / s, s };
}

/** Stage px → client (viewport) px — for overlays drawn outside the scaled stage (selection handles, guides). */
export function stageToClient(p: Point, stageEl: Element, W: number = STAGE.width): Point {
  const r = stageEl.getBoundingClientRect();
  const s = r.width / W || 1;
  return { x: r.left + p.x * s, y: r.top + p.y * s };
}

/** Bounding box of a DOM element inside the stage, in stage px (for selection outlines / hit-testing in editors). */
export function elementStageRect(el: Element, stageEl: Element, W: number = STAGE.width) {
  const r = el.getBoundingClientRect();
  const sr = stageEl.getBoundingClientRect();
  const s = sr.width / W || 1;
  return { x: (r.left - sr.left) / s, y: (r.top - sr.top) / s, w: r.width / s, h: r.height / s };
}

/* ───────────────────────── drawings & pins ───────────────────────── */

export interface DrawingRect {
  /** top-left of the drawing image inside the viewport box, CSS px */
  x: number;
  y: number;
  /** rendered size, CSS px */
  w: number;
  h: number;
  /** CSS px per natural (drawing) px */
  k: number;
}

/**
 * Where the drawing image sits inside its viewport box: contain-fit × `scale` (MASSHTAB), centred, plus an
 * editor pan. Pins are positioned in the same box, so (u, v) always lands on the same feature of the artwork.
 */
export function drawingRect(box: Size, nat: Size, scale = 1, pan: Point = { x: 0, y: 0 }): DrawingRect {
  const fit = Math.min(box.w / nat.w, box.h / nat.h);
  const k = (Number.isFinite(fit) && fit > 0 ? fit : 1) * scale;
  const w = nat.w * k;
  const h = nat.h * k;
  return { x: (box.w - w) / 2 + pan.x, y: (box.h - h) / 2 + pan.y, w, h, k };
}

export const pinToBox = (r: DrawingRect, at: { u: number; v: number }): Point => ({ x: r.x + at.u * r.w, y: r.y + at.v * r.h });

export const clamp01 = (n: number) => clamp(n, 0, 1);
/** u/v are stored with 5 decimals (§8.4). */
export const roundUV = (n: number) => Math.round(clamp01(n) * 1e5) / 1e5;

/**
 * Pointer → normalised drawing coordinates. Pass the drawing box element (`[data-drawing-box]`, the element whose
 * box is exactly the image). `inside` is false when the pointer is outside the artwork (clicks there are ignored).
 */
export function clientToUV(e: ClientPoint, boxEl: Element): { u: number; v: number; inside: boolean } {
  const r = boxEl.getBoundingClientRect();
  const u = (e.clientX - r.left) / (r.width || 1);
  const v = (e.clientY - r.top) / (r.height || 1);
  return { u: roundUV(u), v: roundUV(v), inside: u >= 0 && u <= 1 && v >= 0 && v <= 1 };
}

/** Radius used for label placement (design px). The same in the editor and the viewer so labels match (WYSIWYG). */
export const PIN_DOT_RADIUS = 7;

/**
 * Label card box relative to the pin point (all design px): distance from the dot edge along `side`,
 * `offset` across it (§6.7 "NUQTADAN MASOFA" / "YUQORIGA / PASTGA SILJITISH").
 */
export type LabelSide = (typeof LABEL_SIDES)[number];

export function labelBox(p: Point, size: Size, side: LabelSide, distance: number, offset: number, dotR = PIN_DOT_RADIUS) {
  switch (side) {
    case 'right':
      return { left: p.x + dotR + distance, top: p.y - size.h / 2 + offset };
    case 'left':
      return { left: p.x - dotR - distance - size.w, top: p.y - size.h / 2 + offset };
    case 'top':
      return { left: p.x - size.w / 2 + offset, top: p.y - dotR - distance - size.h };
    case 'bottom':
      return { left: p.x - size.w / 2 + offset, top: p.y + dotR + distance };
  }
}
